































import { debounce } from 'debounce';
import { Component, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { MobileGetter } from 'frontend/types';

interface Occupation {
  title: string;
  cssClass: 'blue' | 'red' | 'orange';
}

interface Principle {
  className: string;
  title: string;
  description: string;
}

@Component({
  computed: {
    ...mapGetters('pageHeader', ['isMobile']),
  },
})
export default class PrinciplesOfLearning extends Vue {
  private isMobile!: MobileGetter;

  private screenWidth = 1250;
  private occupationIndex = 0;
  private occupationList: Occupation[] = [
    {
      title: 'mathematician',
      cssClass: 'blue',
    },
    {
      title: 'mechanical engineer',
      cssClass: 'red',
    },
    {
      title: 'scientist',
      cssClass: 'orange',
    },
    {
      title: 'statistician',
      cssClass: 'blue',
    },
    {
      title: 'data analyst',
      cssClass: 'red',
    },
    {
      title: 'physicist',
      cssClass: 'orange',
    },
  ];
  private occupationString = '';
  private principles: Principle[] = [
    {
      className: 'master',
      title: "Master, don't memorize",
      description: `Forget everything you memorized, and learn how STEM actually works. Build \
          a complete understanding from the bottom up.`,
    },
    {
      className: 'solve-problems',
      title: 'Solve problems',
      description: `Get instant feedback and improve rapidly. Research shows that problem \
          solving is more effective than watching lectures.`,
    },
    {
      className: 'build-intuition',
      title: 'Build STEM intuition',
      description: `See STEM in a new way with one-of-a-kind interactive \
          visualizations and intuitive explanations.`,
    },
    {
      className: 'struggle-required',
      title: 'Some struggle required',
      description: `The path to excellence is simple, but not easy. Anyone can succeed in \
          STEM, but some struggle is required.`,
    },
  ];

  private debouncedHandleResize = debounce(this.handleResize, 50);

  private get currentOccupation(): Occupation {
    return this.occupationList[this.occupationIndex];
  }

  private get titleNeedsBreak(): boolean {
    return this.isMobile(this.$mq) || this.screenWidth <= 960;
  }

  private deleteOccupation(): Promise<void> {
    return new Promise(resolve => {
      const deleter = setInterval(() => {
        if (this.occupationString.length === 0) {
          clearInterval(deleter);
          resolve();
        } else {
          this.occupationString = this.occupationString.substring(
            0,
            this.occupationString.length - 1,
          );
        }
      }, 80);
    });
  }

  private typeOccupation(): Promise<void> {
    return new Promise(resolve => {
      const typer = setInterval(() => {
        const { title } = this.currentOccupation;
        if (this.occupationString.length === title.length) {
          clearInterval(typer);
          resolve();
        } else {
          this.occupationString = title.substring(0, this.occupationString.length + 1);
        }
      }, 100);
    });
  }

  private async runTypewriter(): Promise<any> {
    const delayBeforeTyping = 100;
    const delayBeforeDeleting = 1000;

    await this.deleteOccupation();

    if (this.occupationIndex === this.occupationList.length - 1) {
      this.occupationIndex = 0;
    } else {
      this.occupationIndex += 1;
    }

    setTimeout(async () => {
      await this.typeOccupation();
      setTimeout(() => this.runTypewriter(), delayBeforeDeleting);
    }, delayBeforeTyping);
  }

  private handleResize(): void {
    this.screenWidth = document.body.offsetWidth;
  }

  private created(): void {
    this.occupationString = this.currentOccupation.title;
  }

  private mounted(): void {
    this.handleResize();
    window.addEventListener('resize', this.debouncedHandleResize);
    this.runTypewriter();
  }

  private destroyed(): void {
    window.removeEventListener('resize', this.debouncedHandleResize);
  }
}
